import { isPointInRect } from '../engine/hex/hitTest';
import type { Point } from '../engine/hex/types';
import type { HexMapState } from '../engine/render/renderer';
import { getContentRect } from '../engine/render/modalLayout';
import { applyModalAction } from '../engine/input/actionController';
import type { AbstractModal } from './AbstractModal';
import { isTutorialActionAllowed } from '../tutorials/runtime';

const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

export class ModalManager {
    constructor(private state: HexMapState) {}

    getModalAtPoint(point: Point): { modal: AbstractModal; index: number } | null {
        for (let i = this.state.modals.length - 1; i >= 0; i -= 1) {
            const modal = this.state.modals[i];
            if (isPointInRect(point, modal.rect)) return { modal, index: i };
        }
        return null;
    }

    bringToFront(index: number): void {
        if (index === this.state.modals.length - 1) return;
        const [modal] = this.state.modals.splice(index, 1);
        this.state.modals.push(modal);
    }

    handleCloseOrAction(point: Point, modal: AbstractModal): boolean {
        if (isPointInRect(point, modal.closeRect)) {
            if (
                this.state.tutorial &&
                !this.state.tutorial.completed &&
                !modal.allowCloseDuringTutorial
            ) {
                return true;
            }
            this.state.modals = this.state.modals.filter((m) => m.id !== modal.id);
            this.state.activeModalId =
                this.state.modals.length > 0
                    ? this.state.modals[this.state.modals.length - 1].id
                    : null;
            return true;
        }

        const actionHit = modal.actionRects.find(
            (action) => action.enabled && isPointInRect(point, action.rect),
        );
        if (actionHit) {
            if (!isTutorialActionAllowed(this.state, actionHit.id)) return true;
            if (modal.handleAction(actionHit.id, this.state)) return true;
            return applyModalAction(this.state, modal, actionHit.id);
        }

        return false;
    }

    handleScroll(point: Point, deltaY: number): boolean {
        const hit = this.getModalAtPoint(point);
        if (!hit) return false;
        const { modal, index } = hit;
        this.bringToFront(index);
        if (
            modal.tabScroll.rect &&
            modal.tabScroll.max > 0 &&
            isPointInRect(point, modal.tabScroll.rect)
        ) {
            modal.tabScroll.offset = clamp(modal.tabScroll.offset + deltaY, 0, modal.tabScroll.max);
            this.state.activeModalId = modal.id;
            return true;
        }
        const contentRect = getContentRect(modal.rect);
        const maxScroll = Math.max(0, modal.contentHeight - contentRect.h);
        modal.scroll = clamp(modal.scroll + deltaY, 0, maxScroll);
        this.state.activeModalId = modal.id;
        return true;
    }
}
